import { type ReactNode } from 'react'

import { Section } from 'core/ui/components/section/Section'
import useWindowWidth from 'core/ui/hooks/useWindowWidth'

import { intSizes } from 'legacy/styles/screen'
import user from 'legacy/utils/user'

import { analytics } from '../../core/utils/analytics'
import { type AgnosticShipment } from '../domain/AgnosticShipment'
import {
  formatAgnosticIdentifiers,
  formatSectionCountSubtitle,
  formatStatusTagLabel,
  formatStatusTagVariant,
} from '../presenters/agnosticShipmentPresenter'
import { ShipmentCard } from './ShipmentCard'

export type AgnosticShipperListSectionProps = {
  count: number
  shipments?: Array<AgnosticShipment>
  title: string
  actions?: ReactNode
  onActionClick: (shipmentURL: string) => void
}

export function AgnosticShipperListSection({
  count,
  shipments,
  title,
  actions,
  onActionClick,
}: AgnosticShipperListSectionProps) {
  const windowWidth = useWindowWidth()
  const compact = windowWidth <= intSizes.md

  if (!shipments || shipments.length === 0) return null

  /**
   * If you're testing locally you'll be redirected to https://carrier.staging.loadsmart.com. Replace the argument
   * `shipment.shipmentURL` to `shipment.shipmentURL.replace(/.*\.com/g, 'http://localhost:8080')` to work locally.
   */
  function handleOnCardClick(shipment: AgnosticShipment) {
    analytics.track('Clicked: Shipment Card', {
      shipper: shipment.shipper.companyName,
      status: shipment.status,
      logged: user.isLoggedIn(),
    })

    onActionClick?.(shipment.shipmentURL)
  }

  return (
    <Section title={title} subtitle={formatSectionCountSubtitle(count)} actions={actions}>
      {shipments.map(shipment => (
        <ShipmentCard
          key={shipment.uuid}
          shipment={shipment}
          getStatus={() => ({
            variant: formatStatusTagVariant(shipment),
            label: formatStatusTagLabel(shipment),
          })}
          getIdentifiers={() => formatAgnosticIdentifiers(compact, shipment)}
          onCardClick={() => handleOnCardClick(shipment)}
        />
      ))}
    </Section>
  )
}
